import { Language, ReportType } from "@/domain/records/types";

/**
 * Domain Model for Report Generation Request
 * Clean Architecture - Domain Layer
 */
export class GenerationRequest {
  constructor(
    public readonly recordId: string,
    public readonly reportTypes: ReportType[],
    public readonly customPrompt?: string,
    public readonly goals?: string, // Research Context
    public readonly guide?: string, // Interview Guide
    public readonly notes?: string, // Additional Info
    public readonly participantCount?: number,
    public readonly respondentNames: string[] = [],
    public readonly language?: Language | string,
    public readonly reportLanguage?: Language | string,
    public readonly isWithTranscript: boolean = true,
    public readonly isWithInsightReport: boolean = true,
    public readonly sttProvider?: "auto" | "assembly" | "soniox"
  ) {}

  /**
   * Convert content language value to API format.
   */
  get languageForApi(): string | undefined {
    const normalized = this.normalizeLanguageValue(this.language);

    if (!normalized || normalized === Language.SameAsTranscript) {
      return undefined;
    }

    return normalized;
  }

  /**
   * Convert report language value to API format.
   */
  get reportLanguageForApi(): string | undefined {
    const normalized = this.normalizeLanguageValue(this.reportLanguage);

    if (!normalized) {
      return undefined;
    }

    return normalized;
  }

  get sttProviderForApi(): "auto" | "assembly" | "soniox" | undefined {
    return this.normalizeSttProvider(this.sttProvider);
  }

  /**
   * Get filtered respondent names (remove empty strings)
   */
  get filteredRespondentNames(): string[] {
    return this.respondentNames.filter((name) => name.trim().length > 0);
  }

  /**
   * Check if request includes transcript generation
   */
  get includesTranscript(): boolean {
    return this.isWithTranscript;
  }

  /**
   * Check if request includes report generation
   */
  get includesReports(): boolean {
    return this.isWithInsightReport && this.reportTypes.length > 0;
  }

  /**
   * Validate request before submission
   */
  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Allow empty reportTypes if transcript is selected OR if insight reports are not enabled
    const shouldRequireReportTypes =
      this.isWithInsightReport === true && !this.isWithTranscript;

    if (shouldRequireReportTypes && this.reportTypes.length === 0) {
      errors.push("At least one report type must be selected");
    }

    if (
      this.reportTypes.includes("Custom") &&
      (!this.customPrompt || this.customPrompt.trim().length === 0)
    ) {
      errors.push(
        "Custom prompt is required when Custom report type is selected"
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private static readonly LANGUAGE_CANONICAL_MAP: Record<string, string> = {
    english: Language.English,
    "global english": Language.English,
    "australian english": Language.English,
    "british english": Language.English,
    "us english": Language.English,
    russian: Language.Russian,
    spanish: Language.Spanish,
    french: Language.French,
    german: Language.German,
    italian: Language.Italian,
    portuguese: Language.Portuguese,
    dutch: Language.Dutch,
    chinese: Language.Chinese,
    japanese: Language.Japanese,
    korean: Language.Korean,
    hindi: Language.Hindi,
    turkish: Language.Turkish,
    polish: Language.Polish,
    ukrainian: Language.Ukrainian,
    romanian: Language.Romanian,
    czech: Language.Czech,
    swedish: Language.Swedish,
    hungarian: Language.Hungarian,
    finnish: Language.Finnish,
    norwegian: Language.Norwegian,
    "norwegian nynorsk": Language.NorwegianNynorsk,
    danish: Language.Danish,
    greek: Language.Greek,
    bulgarian: Language.Bulgarian,
    croatian: Language.Croatian,
    slovak: Language.Slovak,
    slovenian: Language.Slovenian,
    lithuanian: Language.Lithuanian,
    latvian: Language.Latvian,
    estonian: Language.Estonian,
    icelandic: Language.Icelandic,
    catalan: Language.Catalan,
    galician: Language.Galician,
    basque: Language.Basque,
    breton: Language.Breton,
    welsh: Language.Welsh,
    arabic: Language.Arabic,
    hebrew: Language.Hebrew,
    persian: Language.Persian,
    urdu: Language.Urdu,
    bengali: Language.Bengali,
    tamil: Language.Tamil,
    gujarati: Language.Gujarati,
    kannada: Language.Kannada,
    malayalam: Language.Malayalam,
    telugu: Language.Telugu,
    thai: Language.Thai,
    marathi: Language.Marathi,
    indonesian: Language.Indonesian,
    malay: Language.Malay,
    vietnamese: Language.Vietnamese,
    tagalog: Language.Tagalog,
    swahili: Language.Swahili,
    afrikaans: Language.Afrikaans,
    kazakh: Language.Kazakh,
    uzbek: Language.Uzbek,
    turkmen: Language.Turkmen,
    azerbaijani: Language.Azerbaijani,
    belarusian: Language.Belarusian,
    armenian: Language.Armenian,
    mongolian: Language.Mongolian,
    nepali: Language.Nepali,
    sinhala: Language.Sinhala,
    lao: Language.Lao,
    albanian: Language.Albanian,
    amharic: Language.Amharic,
    assamese: Language.Assamese,
    bashkir: Language.Bashkir,
    bosnian: Language.Bosnian,
    faroese: Language.Faroese,
    georgian: Language.Georgian,
    haitian: Language.Haitian,
    hausa: Language.Hausa,
    hawaiian: Language.Hawaiian,
    javanese: Language.Javanese,
    latin: Language.Latin,
    lingala: Language.Lingala,
    luxembourgish: Language.Luxembourgish,
    macedonian: Language.Macedonian,
    malagasy: Language.Malagasy,
    maltese: Language.Maltese,
    maori: Language.Maori,
    occitan: Language.Occitan,
    panjabi: Language.Panjabi,
    pashto: Language.Pashto,
    sanskrit: Language.Sanskrit,
    serbian: Language.Serbian,
    shona: Language.Shona,
    sindhi: Language.Sindhi,
    somali: Language.Somali,
    sundanese: Language.Sundanese,
    tajik: Language.Tajik,
    tatar: Language.Tatar,
    yiddish: Language.Yiddish,
    yoruba: Language.Yoruba,
  };

  private normalizeLanguageValue(
    language?: Language | string
  ): string | undefined {
    if (!language) {
      return undefined;
    }

    const raw = String(language).trim();
    if (!raw) {
      return undefined;
    }

    const lower = raw.toLowerCase();

    if (lower === "auto") {
      return undefined;
    }

    if (lower === "same as transcript" || lower === "same_as_transcript") {
      return Language.SameAsTranscript;
    }

    return GenerationRequest.LANGUAGE_CANONICAL_MAP[lower] ?? raw;
  }

  private normalizeSttProvider(
    value?: string
  ): "auto" | "assembly" | "soniox" | undefined {
    if (!value) {
      return undefined;
    }

    const normalized = value.trim().toLowerCase();
    if (normalized === "auto") {
      return "auto";
    }
    if (normalized === "assembly") {
      return "assembly";
    }
    if (normalized === "soniox") {
      return "soniox";
    }
    return undefined;
  }
}
